/**
 * Plain text into the editor's HTML.
 *
 * The composer became a rich-text box, but three things still arrive as plain
 * text and have to be poured into it: a forward's quoted original, a template's
 * text part, and the AI's draft reply. None of them is going to grow HTML, and
 * dropping the text in raw would show the reader their own `<` characters.
 *
 * The other direction already exists (`emailBuilder/text` `htmlToText`) and is
 * what produces the mail's plain-text alternative.
 *
 * Pure and DOM-free, so the conversion can be tested without a browser.
 */

/** Enough escaping for text about to become element content. */
function esc(s: string): string {
  return s.replace(/[&<>]/g, (c) => (c === "&" ? "&amp;" : c === "<" ? "&lt;" : "&gt;"));
}

/**
 * One paragraph per blank-line-separated block, `<br>` for a single newline.
 *
 * That is the shape a person means when they type into a textarea, and it is
 * the shape tiptap would have produced had they typed it into the editor - so
 * a forwarded message opens looking like what it will send, rather than as one
 * paragraph the writer then has to break up by hand.
 */
export function textToHtml(text: string): string {
  const trimmed = String(text ?? "").replace(/\r\n/g, "\n").trim();
  if (!trimmed) return "";
  return trimmed
    .split(/\n{2,}/)
    .map((block) => `<p>${esc(block).split("\n").join("<br>")}</p>`)
    .join("");
}

/**
 * Is there anything in this HTML a reader would see?
 *
 * tiptap never returns an empty string: an untouched editor holds `<p></p>`,
 * and a cleared one holds the same. Send would have accepted that as a message
 * and the recipient would have got a blank mail, so emptiness is asked here
 * rather than of the string's length.
 */
export function isEmptyHtml(html: string): boolean {
  return (
    String(html ?? "")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim().length === 0
  );
}
